import {
  getColor,
  getColorPalette,
  isOklchCompliant,
} from '@theme/utils/colors';

import type { ColorType, Dict } from '@theme/theme.d';

/**
 *   Color → Settings
 * ------------------------------------------------------ */
export const ColorSettings = {
  MAX_LIGHTNESS: 96,
  MIN_LIGHTNESS: 9,
  PRECISION: 6,
  SPACE: isOklchCompliant ? 'oklch' : 'srgb',
};

export const ColorDefaults: Record<string, ColorType> = {
  BLACK: '#000000',
  DK_BLUE: '#41adff',
  DK_GOLD: '#F0D884',
  DK_GREEN: '#4fff42',
  DK_ORANGE: '#ff9341',
  DK_PINK: '#ff41ad',
  DK_PURPLE: '#9341ff',
  DK_RED: '#ff0000',
  DK_RED_LIGHT: '#ffebeb',
  DK_YELLOW: '#ccff50',
  GRAY_100: '#e7e7e7',
  GRAY_200: '#cccccc',
  GRAY_300: '#b4b4b4',
  GRAY_400: '#989898',
  GRAY_50: '#f5f5f5',
  GRAY_500: '#808080',
  GRAY_600: '#656565',
  GRAY_700: '#4a4a4a',
  GRAY_800: '#313131',
  GRAY_850: '#252525',
  GRAY_900: '#1a1a1a',
  SHD_GREEN_DARK: '#183710',
  SHD_GREEN: '#1c8700',
  SHD_YELLOW: '#ccff50',
  WHITE: '#ffffff',
};

export const ColorBrands: Record<string, ColorType> = {
  APPLE: '#000000',
  FACEBOOK: '#0165E1',
  GITHUB: '#000000',
  GOOGLE: '#307DEF ',
  POCKETRADAR_GRAY: '#1D2024',
  POCKETRADAR_RED: '#E61837',
  SPORTSRECRUITS_BLUE: '#07245C',
  SPORTSRECRUITS_RED: '#FE3F3D',
  TIKTOK: '#25F4EE',
  TWITCH: '#9146FF',
  YOUTUBE: '#FF0000',
};

/**
 *   Color → Palettes
 * ------------------------------------------------------ */

const colorText = {
  text: 'gray-900',
  'text-strong': 'gray-900',
  'text-weak': 'gray-400',
  'text-xweak': 'gray-300',
};

const colorComponentDefaults = {
  border: 'gray-300',
  control: 'gray-300',
  focus: 'black',
  'graph-0': 'gray-100',
  'graph-1': 'gray-200',
  'graph-2': 'gray-300',
  'graph-3': 'gray-400',
  'graph-4': 'gray-500',
  icon: 'gray-500',
  placeholder: 'gray-200',
  selected: 'brand-200',
};

const colorStatus = {
  'status-critical': 'dk-red-500',
  'status-disabled': 'gray-500',
  'status-error': 'dk-red-500',
  'status-ok': 'dk-green-300',
  'status-unknown': 'gray-500',
  'status-warning': 'dk-red',
  'status-warning-light': 'dk-red-light',
};

/**
 *   Color defaults
 * ------------------------------------------------------ */

export const colors: Dict = {
  ...colorComponentDefaults,
  ...colorText,
  ...colorStatus,
  ...getColorPalette('white', ColorDefaults.WHITE),
  ...getColorPalette('black', ColorDefaults.BLACK),
  ...getColorPalette('dk-blue', ColorDefaults.DK_BLUE, true),
  ...getColorPalette('dk-gold', ColorDefaults.DK_GOLD, true),
  ...getColorPalette('dk-green', ColorDefaults.DK_GREEN, true),
  ...getColorPalette('dk-orange', ColorDefaults.DK_ORANGE, true),
  ...getColorPalette('dk-pink', ColorDefaults.DK_PINK, true),
  ...getColorPalette('dk-purple', ColorDefaults.DK_PURPLE, true),
  ...getColorPalette('dk-red', ColorDefaults.DK_RED, true),
  ...getColorPalette('dk-yellow', ColorDefaults.DK_YELLOW, true),
  ...getColorPalette('shd-green-dark', ColorDefaults.SHD_GREEN_DARK, true),
  ...getColorPalette('shd-yellow', ColorDefaults.SHD_YELLOW, true),
  ...getColorPalette(
    ['shd-green', 'primary', 'brand'],
    ColorDefaults.SHD_GREEN,
    true
  ),
  ...getColorPalette(
    ['gray-50', 'light-50', 'dark-50', 'neutral-50'],
    ColorDefaults.GRAY_50
  ),
  ...getColorPalette(
    ['gray-100', 'light-100', 'dark-100', 'neutral-100'],
    ColorDefaults.GRAY_100
  ),
  ...getColorPalette(
    ['gray-200', 'light-200', 'dark-200', 'neutral-200'],
    ColorDefaults.GRAY_200
  ),
  ...getColorPalette(
    ['gray-300', 'light-300', 'dark-300', 'neutral-300'],
    ColorDefaults.GRAY_300
  ),
  ...getColorPalette(
    ['gray-400', 'light-400', 'dark-400', 'neutral-400'],
    ColorDefaults.GRAY_400
  ),
  ...getColorPalette(
    ['gray-500', 'light-500', 'dark-500', 'neutral-500'],
    ColorDefaults.GRAY_500
  ),
  ...getColorPalette(
    ['gray-600', 'light-600', 'dark-600', 'neutral-600'],
    ColorDefaults.GRAY_600
  ),
  ...getColorPalette(
    ['gray-700', 'light-700', 'dark-700', 'neutral-700'],
    ColorDefaults.GRAY_700
  ),
  ...getColorPalette(
    ['gray-800', 'light-800', 'dark-800', 'neutral-800'],
    ColorDefaults.GRAY_800
  ),
  ...getColorPalette(
    ['gray-850', 'light-850', 'dark-850', 'neutral-850'],
    ColorDefaults.GRAY_850
  ),
  ...getColorPalette(
    ['gray-900', 'light-900', 'dark-900', 'neutral-900'],
    ColorDefaults.GRAY_900
  ),
  'dk-red-light': getColor(ColorDefaults.DK_RED_LIGHT),
  'pocket-radar-gray': getColor(ColorBrands.POCKETRADAR_GRAY),
  'pocket-radar-red': getColor(ColorBrands.POCKETRADAR_RED),
  'sports-recruits-blue': getColor(ColorBrands.SPORTSRECRUITS_BLUE),
  'sports-recruits-red': getColor(ColorBrands.SPORTSRECRUITS_RED),
  apple: getColor(ColorBrands.APPLE),
  facebook: getColor(ColorBrands.FACEBOOK),
  github: getColor(ColorBrands.GITHUB),
  google: getColor(ColorBrands.GOOGLE),
  tiktok: getColor(ColorBrands.TIKTOK),
  twitch: getColor(ColorBrands.TWITCH),
  youtube: getColor(ColorBrands.YOUTUBE),
};

// export const backgroundGradients = {
//   'primary-gradient': getLinearGradient(
//     'left',
//     '#1c8700',
//     '#ccff50'
//   ),
//   // 'modal-curtain': getRadialGradient(
//   //   'ellipse',
//   //   'center',
//   //   colors['black-alpha-90'] as string,
//   //   colors['black-alpha-5'] as string
//   // ),
// };
